import { z } from "zod/v4";

import type { Weekday } from "./weekday";
import { recurrenceToWeekdays, weekdays, WEEKDAYS } from "./weekday";

export type PassStatus =
  | "active"
  | "upcoming"
  | "expired"
  | "revoked"
  | "used"
  | "pending"
  | "rejected"
  | "off_schedule"
  /** Self-fill visitor has not submitted their details yet (spec §3.1). */
  | "incomplete";

/**
 * The statuses that mean "this guest is still going to turn up".
 *
 * Shared by the console's expected-guests list, the syndic dashboard and the
 * gate station's forecast, so all three agree on what "expected" means.
 *
 * `off_schedule` is deliberately included: it is a statement about the CURRENT
 * moment, not about the pass. A Mon/Thu recurring pass reads `off_schedule` on
 * a Tuesday and `active` on a Thursday, so excluding it would empty Thursday's
 * forecast. Whether a given DAY counts is a weekday question, answered by
 * `isOnRecurringSchedule` — never by this set.
 *
 * `incomplete` is expected too: reception may admit an unfilled visitor after
 * checking ID (spec §8).
 */
export const EXPECTED_PASS_STATUSES: ReadonlySet<PassStatus> = new Set([
  "active",
  "upcoming",
  "pending",
  "off_schedule",
  "incomplete",
]);

/**
 * The statuses a manager can FILTER on. `off_schedule` is absent on purpose:
 * it describes this minute, not the pass (a Mon/Thu pass reads off_schedule on
 * Tuesday and active on Thursday), and filtering on it would mean parsing
 * `recurrence` in SQL. Server filters treat it as `active`; the pill still
 * shows it. Spec 2026-09-26-invitations-table §1.
 */
export const FILTERABLE_PASS_STATUSES = [
  "active",
  "upcoming",
  "expired",
  "revoked",
  "used",
  "pending",
  "rejected",
  "incomplete",
] as const satisfies readonly PassStatus[];
export type FilterablePassStatus = (typeof FILTERABLE_PASS_STATUSES)[number];
export const filterablePassStatusSchema = z.enum(FILTERABLE_PASS_STATUSES);

export function toFilterableStatus(s: PassStatus): FilterablePassStatus {
  return s === "off_schedule" ? "active" : s;
}

const OFFSET_SHAPED = /^[+-]\d{1,2}(:?\d{2})?$/;

/** Valid IANA zone identifiers, resolved once. `Intl.supportedValuesOf` is
 *  Node 18+/modern-browser; on a runtime without it we fall back to probing
 *  the candidate with `Intl.DateTimeFormat`, which throws on an unknown zone.
 *  Both paths reject a bare offset like "+01:00", which is the case that
 *  matters — an offset is exactly the frozen shift this column exists to
 *  avoid storing. */
function isValidTimeZone(tz: string): boolean {
  // Reject offsets outright: ECMA-402 lets Intl accept "+01:00" as a time
  // zone, and an offset is exactly the frozen shift this column exists to
  // avoid storing. Match the SHAPE — four real IANA zones contain a hyphen
  // (America/Port-au-Prince and friends).
  if (!tz || OFFSET_SHAPED.test(tz) || /^UTC[+-]/.test(tz)) return false;
  try {
    const supported = Intl.supportedValuesOf?.("timeZone");
    if (supported) return supported.includes(tz);
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const timeZoneSchema = z
  .string()
  .refine(isValidTimeZone, { message: "Fuseau horaire invalide." });

/** "Africa/Casablanca" -> "Casablanca". The city is what a reader recognizes;
 *  the region prefix is noise in a UI label. */
export function zoneLabel(tz: string): string {
  const city = tz.split("/").pop() ?? tz;
  return city.replaceAll("_", " ");
}

/** Note: `Intl.supportedValuesOf("timeZone")` omits some legacy aliases (e.g.
 *  `Asia/Calcutta`). We only ever write values chosen from the same list in
 *  the settings picker (Task 11), so the set is self-consistent. */

/** Sun..Sat (Intl "short" weekday, and JS Date#getDay order) → canonical
 *  French weekday codes. */
const INTL_WEEKDAY_TO_CODE: Record<string, Weekday> = {
  Sun: "dim",
  Mon: "lun",
  Tue: "mar",
  Wed: "mer",
  Thu: "jeu",
  Fri: "ven",
  Sat: "sam",
};
const JS_DAY_INDEX_TO_CODE: Weekday[] = [
  "dim",
  "lun",
  "mar",
  "mer",
  "jeu",
  "ven",
  "sam",
];

/** `Intl.DateTimeFormat` construction is the expensive part of formatting —
 *  `derivePassStatus` runs per row, so an unmemoized formatter means a
 *  500-row render builds ~1000 of them. Keyed by `timeZone`; a zone that
 *  throws (bad/unknown timeZone) is never inserted, so it keeps throwing on
 *  every call rather than poisoning the cache with a broken formatter. */
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatterForZone(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatterCache.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(timeZone, formatter);
  }
  return formatter;
}

/** Full Y/M/D + weekday + H/M parts of `d` as read in `timeZone`. Shared by
 *  `wallClockInZone` (weekday/minutes only) and `zonedWallClockToUtc` (needs
 *  the calendar date too, to correct its UTC guess). Throws on an
 *  unsupported/unknown `timeZone` or incomplete Intl output — both callers
 *  catch and fall back to runtime-local behavior. */
function partsInZone(
  d: Date,
  timeZone: string,
): { year: number; month: number; day: number; weekday: Weekday; hour: number; minute: number } {
  const parts = formatterForZone(timeZone).formatToParts(d);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  const weekdayPart = get("weekday");
  const yearPart = get("year");
  const monthPart = get("month");
  const dayPart = get("day");
  const hourPart = get("hour");
  const minutePart = get("minute");
  const weekday = weekdayPart ? INTL_WEEKDAY_TO_CODE[weekdayPart] : undefined;
  if (
    !weekday ||
    yearPart === undefined ||
    monthPart === undefined ||
    dayPart === undefined ||
    hourPart === undefined ||
    minutePart === undefined
  ) {
    throw new Error("incomplete Intl.DateTimeFormat parts");
  }
  return {
    year: Number(yearPart),
    month: Number(monthPart) - 1, // 0-based, like Date
    day: Number(dayPart),
    weekday,
    hour: Number(hourPart),
    minute: Number(minutePart),
  };
}

/** The calendar date `d` falls on, as read in `timeZone`. `month` is 0-based,
 *  like Date. */
export function calendarDayInZone(
  d: Date,
  timeZone: string,
): { year: number; month: number; day: number } {
  const { year, month, day } = partsInZone(d, timeZone);
  return { year, month, day };
}

/** "08:30" -> 510. Returns null for an empty or unparseable string. */
export function hhmmToMinutes(s: string | undefined): number | null {
  if (!s) return null;
  const [h, m] = s.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h! < 0 || h! > 23 || m! < 0 || m! > 59) return null;
  return h! * 60 + m!;
}

/** 510 -> "08:30". */
export function minutesToHhmm(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Residence-local wall clock; falls back to the runtime's local time if the
 *  Intl timeZone lookup throws (older Hermes). */
export function wallClockInZone(
  d: Date,
  timeZone: string,
): { weekday: Weekday; minutes: number } {
  try {
    const { weekday, hour, minute } = partsInZone(d, timeZone);
    return { weekday, minutes: hour * 60 + minute };
  } catch {
    // Older Hermes (mobile) has limited/no Intl timeZone support. Fall back
    // to the runtime's local time rather than throwing — this only matters
    // for scheduling display/enforcement, not security-critical logic.
    return {
      weekday: JS_DAY_INDEX_TO_CODE[d.getDay()] ?? "dim",
      minutes: d.getHours() * 60 + d.getMinutes(),
    };
  }
}

/** The daily window implied by a recurring pass's stored instants, read in
 *  `timeZone`. This is the ONLY correct way to interpret pre-migration rows,
 *  because it is exactly what `isOnRecurringSchedule` did before the daily
 *  columns existed — including its NULL defaults (0 and 1439).
 *
 *  NOT live code: it has no production caller, and adding one would be a
 *  mistake — reading the window off validFrom/validTo is the very defect the
 *  minute columns replaced. It is exported solely as the test oracle for
 *  migration 0031's backfill, so the SQL and the JS interpretation of a
 *  pre-migration row cannot drift apart. Delete it once no test needs it. */
export function deriveDailyWindow(
  validFrom: Date | string | null,
  validTo: Date | string | null,
  timeZone: string,
): { start: number; end: number } {
  return {
    start: validFrom ? wallClockInZone(new Date(validFrom), timeZone).minutes : 0,
    end: validTo ? wallClockInZone(new Date(validTo), timeZone).minutes : 1439,
  };
}

/** The instant whose wall clock in `timeZone` is the given calendar date at
 *  h:mm. Inverse of wallClockInZone. Falls back to runtime-local construction
 *  if the Intl lookup throws (older Hermes).
 *
 * Authoring's half of the GATELY-40 timezone invariant: a pass's wall-clock
 * time-of-day must be anchored to the ORGANIZATION's zone, never the author's
 * device. Without this, a client on a device outside that zone (`setHours`,
 * device-local) stores an instant that reads back shifted by the
 * device/organization offset difference — the gate then admits/denies at the
 * wrong wall-clock hour depending on where the pass was authored.
 *
 * `timeZone` is required (GATELY-62): there is no house zone to fall back to,
 * and a silent default is how the old hardcoded constant survived this long. */
export function zonedWallClockToUtc(
  year: number,
  month: number, // 0-based, like Date
  day: number,
  hours: number,
  minutes: number,
  timeZone: string,
): Date {
  try {
    const target = Date.UTC(year, month, day, hours, minutes);
    let t = target;
    // One pass isn't always enough: the initial guess can itself land on the
    // other side of a DST/offset transition from the target instant, so its
    // correction can overshoot. A second pass converges — for an *ordinary*
    // (unambiguous, unskipped) wall clock, that second pass is a no-op
    // because the first pass already landed on the right side of every
    // transition. Africa/Casablanca has no observed case that needs it today
    // (every existing test converges in one pass), but a fractional/DST zone
    // can require it — see the Europe/Paris spring-forward case in
    // zoned-wall-clock.test.ts, which fails after one pass and passes after
    // two. Do not "simplify" this to a single pass.
    //
    // For a *skipped* wall clock (inside a spring-forward gap) the iteration
    // does not monotonically converge — it oscillates between the two
    // instants either side of the gap. The loop count of 2 is not just "does
    // it converge" but a deliberate choice of *which* oscillating value comes
    // out: with 2 iterations, a gap time resolves forward, past the gap
    // (02:30 -> instant reading 03:30); a local time that occurs twice
    // (fall-back ambiguity) resolves to its first occurrence. Both match
    // Temporal's `disambiguation: "compatible"` and the Luxon/date-fns-tz
    // defaults — pinned by the "spring-forward gap" test below so a future
    // refactor can't silently flip it by changing the iteration count.
    for (let i = 0; i < 2; i++) {
      const actual = partsInZone(new Date(t), timeZone);
      const actualAsUtc = Date.UTC(
        actual.year,
        actual.month,
        actual.day,
        actual.hour,
        actual.minute,
      );
      t += target - actualAsUtc;
    }
    return new Date(t);
  } catch {
    // Older Hermes (mobile) — fall back to today's pre-fix behavior
    // (constructing via the runtime's local time) rather than throwing. This
    // is the right *availability* call (never block a syndic from creating a
    // pass), but it is not free: on a runtime whose Intl has no working
    // timeZone lookup, this silently reverts to the device's own clock, so a
    // syndic authoring from outside the organization's zone gets a window
    // shifted by that offset — with no error, no warning, nothing. Note the
    // asymmetry with `wallClockInZone`'s fallback, which is on the
    // *evaluation* side (the gate/server, reading the stored instant back):
    // the two fallbacks don't cancel out. A missing Intl here is a phone
    // authoring a pass; the server evaluating that pass at the gate still has
    // a working Intl and reads the (now-wrong) stored instant correctly in
    // the organization's zone — it has no way to know the instant was
    // authored with a device-local shortcut.
    return new Date(year, month, day, hours, minutes, 0, 0);
  }
}

/** Canonical weekday that comes right before `day` (walks `weekdays`,
 *  lun..dim, cyclically — so `dayBefore("lun")` is "dim"). */
function dayBefore(day: Weekday): Weekday {
  const idx = weekdays.indexOf(day);
  return weekdays[(idx - 1 + weekdays.length) % weekdays.length] ?? day;
}

/** Weekday + daily-hour check only — callers handle the overall window.
 *
 *  The daily window is read from `dailyStartMinutes`/`dailyEndMinutes`, which
 *  are timezone-independent wall-clock minutes. It is deliberately NOT derived
 *  from validFrom/validTo any more: an instant freezes the offset in at
 *  creation, so the enforced hour drifted whenever the zone changed. */
export function isOnRecurringSchedule(
  s: {
    recurrence: string | null;
    dailyStartMinutes: number | null;
    dailyEndMinutes: number | null;
  },
  now: Date,
  timeZone: string,
): boolean {
  const days = recurrenceToWeekdays(s.recurrence ?? "");
  // Fail-open: the `recurrence` zod field is free-form for now (its own
  // doc-comment example uses English codes, "mon,tue"), so legacy rows may
  // not parse into any canonical weekday. A legacy recurring pass must NOT
  // suddenly hard-deny at the gate just because we can't read its schedule —
  // it keeps today's permissive (always-valid-within-window) behavior.
  if (days.length === 0) return true;

  // NULL is only reachable from a failed backfill. These defaults reproduce
  // the pre-migration behaviour (an absent bound meant "no bound") rather
  // than denying on missing data.
  const start = s.dailyStartMinutes ?? 0;
  const end = s.dailyEndMinutes ?? 1439;
  const { weekday: today, minutes: nowMin } = wallClockInZone(now, timeZone);

  if (end >= start) {
    return days.includes(today) && start <= nowMin && nowMin <= end;
  }
  // Overnight window (e.g. 20:00 -> 06:00): allow if today is scheduled and
  // we're past the start, OR yesterday was scheduled and we're still before
  // the end (early-morning tail of last night's window).
  return (
    (days.includes(today) && nowMin >= start) ||
    (days.includes(dayBefore(today)) && nowMin <= end)
  );
}

/** Derives the display status from stored fields.
 *
 * `validFrom`/`validTo` accept `Date | string | null` (not just `Date`) so
 * callers holding wire strings — every web/mobile caller, which gets these
 * off JSON — can call this directly without re-parsing first. Normalized to
 * `Date` internally before comparing; purely a widening, no behavior change
 * for callers that already pass `Date`. */
export function derivePassStatus(
  invite: {
    status: string;
    type: string;
    validFrom: Date | string | null;
    validTo: Date | string | null;
    consumedAt: Date | null;
    recurrence: string | null;
    dailyStartMinutes: number | null;
    dailyEndMinutes: number | null;
  },
  now: Date,
  timeZone: string,
): PassStatus {
  if (invite.status === "revoked") return "revoked";
  if (invite.status === "pending") return "pending";
  if (invite.status === "rejected") return "rejected";
  // Unique passes are single-use: once consumed they're spent regardless of
  // their (still-open) validity window.
  if (invite.type === "one_time" && invite.consumedAt) return "used";
  const validFrom = invite.validFrom ? new Date(invite.validFrom) : null;
  const validTo = invite.validTo ? new Date(invite.validTo) : null;
  if (validFrom && now < validFrom) return "upcoming";
  if (validTo && now > validTo) return "expired";
  if (invite.type === "recurring" && !isOnRecurringSchedule(invite, now, timeZone))
    return "off_schedule";
  return "active";
}

/** Invite-level `consumedAt` for a whole invite, derived from its guests.
 *
 * `invite.list`'s row-level `consumedAt` (packages/api/src/router/invite.ts)
 * is populated from the FIRST guest only — feeding that straight into
 * `derivePassStatus`/`deriveGuestPassStatus` for a summary view makes a
 * multi-guest `one_time` invite read "used" the moment guest 1 enters, even
 * though `scan.validate` (keyed off each guest's own `consumedAt`) still
 * correctly admits guests 2..n. A group invite is only truly spent once
 * EVERY guest has entered.
 *
 * Returns the latest `consumedAt` when all guests are consumed, otherwise
 * `null` (including for an empty `guests` array). */
export function inviteConsumedAt(
  guests: { consumedAt: Date | string | null }[],
): Date | string | null {
  if (guests.length === 0) return null;
  if (!guests.every((g) => g.consumedAt)) return null;
  return guests.reduce<Date | string | null>((latest, g) => {
    if (!latest) return g.consumedAt;
    if (!g.consumedAt) return latest;
    return new Date(g.consumedAt) > new Date(latest) ? g.consumedAt : latest;
  }, null);
}

/** Per-visitor status. The guest's own state dominates (spec 2026-09-23 §3.1):
 *    1. guest or invite revoked      → revoked
 *    2. guest rejected               → rejected
 *    3. guest awaiting_visitor       → expired once the invite's validTo
 *                                      has passed, else incomplete
 *    4. guest pending                → pending
 *    5. validity window / one_time consumption, as `derivePassStatus`.
 *  Scan, pass page, PDF and every list go through this one function. */
export function deriveGuestPassStatus(
  invite: {
    status: string;
    type: string;
    validFrom: Date | string | null;
    validTo: Date | string | null;
    recurrence: string | null;
    dailyStartMinutes: number | null;
    dailyEndMinutes: number | null;
  },
  guest: { status: string; consumedAt: Date | null },
  now: Date,
  timeZone: string,
): PassStatus {
  if (guest.status === "revoked" || invite.status === "revoked") return "revoked";
  if (guest.status === "rejected") return "rejected";
  if (guest.status === "awaiting_visitor") {
    // Unfilled beats the window's start (a future visit is still « en
    // attente »), but not its end: once the visit window has passed there is
    // nothing left to fill in for, so the link is dead and the pass reads
    // expired everywhere — no resend, no « Arrivé », no form.
    const validTo = invite.validTo ? new Date(invite.validTo) : null;
    return validTo && now > validTo ? "expired" : "incomplete";
  }
  if (guest.status === "pending") return "pending";
  return derivePassStatus(
    {
      status: invite.status,
      type: invite.type,
      validFrom: invite.validFrom,
      validTo: invite.validTo,
      consumedAt: guest.consumedAt,
      recurrence: invite.recurrence,
      dailyStartMinutes: invite.dailyStartMinutes,
      dailyEndMinutes: invite.dailyEndMinutes,
    },
    now,
    timeZone,
  );
}

/** "Lun, Mer, Ven · 08:00–18:00" (hours omitted when the window is the full
 *  00:00–23:59 day); "" when recurrence is unparseable.
 *
 *  Reads `dailyStartMinutes`/`dailyEndMinutes` — the same timezone-independent
 *  wall-clock minutes `isOnRecurringSchedule` enforces on — so the label a
 *  reader sees and the window the gate applies cannot drift apart. NULL keeps
 *  the pre-migration meaning of an absent bound (0 / 1439).
 *
 *  Takes NO timeZone, deliberately. The minutes ARE the organization's wall
 *  clock, so there is nothing left to convert, and a required parameter this
 *  function ignores is precisely the hazard GATELY-62 exists to remove — it
 *  invites a caller to hand over the wrong zone and read meaning into it. The
 *  zone belongs to the surrounding sentence, not to this substring: naming it
 *  ("· heure de Casablanca") is `formatRecurringSchedule`'s job — that is the
 *  wrapper every recurring-pass surface calls, and it appends the label to
 *  exactly this string. */
export function recurringScheduleLabel(s: {
  recurrence: string | null;
  dailyStartMinutes: number | null;
  dailyEndMinutes: number | null;
}): string {
  const days = recurrenceToWeekdays(s.recurrence ?? "");
  if (days.length === 0) return "";

  const labelByCode = new Map(WEEKDAYS.map((w) => [w.code, w.label]));
  const daysLabel = days.map((d) => labelByCode.get(d) ?? d).join(", ");

  const start = s.dailyStartMinutes ?? 0;
  const end = s.dailyEndMinutes ?? 1439;
  if (start === 0 && end === 1439) return daysLabel;

  const fmt = (mins: number) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  return `${daysLabel} · ${fmt(start)}–${fmt(end)}`;
}
